import { computed, effect, inject, Injectable, signal } from '@angular/core';
import { WebSocketService } from '../../core/services/web-socket.service';
import { BehaviorSubject, catchError, combineLatest, delay, filter, map, of, Subject, switchMap, take, tap } from 'rxjs';
import { KeyphraseRow } from '../../core/interfaces/KeyphraseExtraction';
import { BaseHttpService } from '../../core/services/base-http.service';
import { ErrorHandlerService } from '../../core/services/error-handler.service';
import { TopicClassification } from '../../core/interfaces/TopicClassification';

@Injectable({
  providedIn: 'root',
})
export class StateService {
  private _webSocketService = inject(WebSocketService);
  private _baseHttpService = inject(BaseHttpService);
  private _errorService = inject(ErrorHandlerService)

  private stateSignal = signal<AppState>({
    topicClassificationTableIncoming: [],
    keyphraseExtractionTableIncoming: [],
    awaitingKeyphraseRefresh: false,
    awaitingTopicsRefresh: false,
    isLoading: false,
    pendingSave: false,
    hasError: false,
    error: undefined,
    topicsActive: true
  });

  topicsWebSocketNotification$ = this._webSocketService.topicsWebSocket$.pipe(
    tap(() => this.updateState({ isLoading: true })),
    // Add intentional minimum delay for UX
    delay(800),
    map((data) => data.refresh),
    tap(() => this.updateState({ isLoading: false, awaitingTopicsRefresh: true })),
    catchError((err) => {
        this.updateState({ isLoading: false, hasError: true, error: this._errorService.handleError(err) });
        return of([]);
    })
  );

  keyphraseWebSocketNotification$ = this._webSocketService.keyphraseWebSocket$.pipe(
    tap(() => this.updateState({ isLoading: true })),
    // Add intentional minimum delay for UX
    delay(800),
    map((data) => data.refresh),
    tap(() => this.updateState({ isLoading: false, awaitingKeyphraseRefresh: true })),
    catchError((err) => {
        this.updateState({ isLoading: false, hasError: true, error: this._errorService.handleError(err) });
        return of([]);
    })
  );

  userRefreshSubject = new BehaviorSubject<boolean>(true);
  userRefresh$ = this.userRefreshSubject.asObservable().pipe(
    tap(() => this.updateState({ isLoading: true })),
    delay(800),
    tap(() => this.updateState({ isLoading: false }))
  );

  keyphraseTableData$ = this._baseHttpService.getKeyphraseTable().pipe(
    map((data) => data.table),
    map((table) => table.map((row) => ({ ...row, id: crypto.randomUUID()})))
  );

  topicTableData$ = this._baseHttpService.getTopicTable().pipe(
    map((data) => data.table)
  );

  getTables$ = this.userRefresh$.pipe(
    filter((refresh) => refresh === true),
    switchMap(() => combineLatest([this.keyphraseTableData$, this.topicTableData$]).pipe(
      tap(([KeyphraseTable, TopicTable]) => {
        this.updateState({
          keyphraseExtractionTableIncoming: KeyphraseTable,
          topicClassificationTableIncoming: TopicTable,
          topicsActive:  TopicTable?.length ? TopicTable[0]?.status ?? true : true
        });

       
      })
      
    )),
    catchError((err) => {
      this.updateState({ isLoading: false, hasError: true, error: this._errorService.handleError(err) });
      return of([]);
    }),
    tap(() => this.updateState({ isLoading: false, awaitingKeyphraseRefresh: false, awaitingTopicsRefresh: false }))
  )

  KeyphraseTable$ = this._baseHttpService.getKeyphraseTable();
  TopicTable$ = this._baseHttpService.getTopicTable();

  private deleteRowsSubject = new Subject<Partial<AppState>>();
  deleteRows$ = this.deleteRowsSubject
    .asObservable()
    .pipe(
      tap((partialState) => this.updateKeyphrasesOnDelete(partialState))
    );

  private editRowsSubject = new Subject<boolean>();
  editRows$ = this.editRowsSubject.asObservable();

  private saveChangesSubject = new Subject<boolean>();
  saveChanges$ = this.saveChangesSubject.asObservable();

  topicClassificationTableIncoming = computed(
    () => this.stateSignal().topicClassificationTableIncoming
  );
  keyphraseExtractionTableIncoming = computed(
    () => this.stateSignal().keyphraseExtractionTableIncoming
  );

  isLoading = computed(() => this.stateSignal().isLoading);
  awaitingTopicsRefresh = computed(() => this.stateSignal().awaitingTopicsRefresh);
  awaitingKeyphraseRefresh = computed(() => this.stateSignal().awaitingKeyphraseRefresh);
  pendingSave = computed(() => this.stateSignal().pendingSave)
  hasError = computed(() => this.stateSignal().hasError)
  error = computed(() => this.stateSignal().error)
  topicsActive = computed(() => this.stateSignal().topicsActive)

  constructor() {
    effect(() => console.log('current state: ', this.stateSignal()));
  }

  private updateState(newState: Partial<AppState>) {
    this.stateSignal.update((oldState) => {
      return { ...oldState, ...newState };
    });
  }

  public updateTopicStatus(active: boolean){
    this.updateState({
      topicsActive: active,
      topicClassificationTableIncoming: this.topicClassificationTableIncoming().map((topic) => {
        return {
          ...topic,
          status: active
        }
      })
    })
  }

  public updateTopics(partialState: Partial<AppState>) {
    this.updateState(partialState);
  }

  public updateKeyphrasesOnDelete(partialState: Partial<AppState>) {
    this.updateState(partialState);
  }

  public updateKeyphrasesTableOnSocketBroadcast(partialState: Partial<AppState>) {
    this.updateState(partialState);
  }

  public confirmTopicsRefresh(){
    this.updateState({
      awaitingTopicsRefresh: false
    })
  }
  public confirmKeyphraseRefresh(){
    this.updateState({
      awaitingKeyphraseRefresh: false
    })
  }

  public promptKeyphraseRefresh(){
    this.updateState({
      awaitingKeyphraseRefresh: true
    })
  }

  public promptTopicsRefresh(){
    this.updateState({
      awaitingTopicsRefresh: true
    })
  }

  public updateLoading(loading: boolean){
    this.updateState({
      isLoading: loading
    })
  }

  public updatePendingChanges(pending: boolean){
    this.updateState({
      pendingSave: pending
    })
  }

  public setStateError<T extends Error>(err: T){
    this.updateState({
      error: err
    })
  }

  public getFreshData(){
    this.userRefreshSubject.next(true);
  }

  public addKeyphrase(newKeyphrase: KeyphraseRow) {
    this.stateSignal.update((state) => ({
      ...state,
      keyphraseExtractionTableIncoming: [...state.keyphraseExtractionTableIncoming, newKeyphrase]
    }));
  }
}

export type AppState = {
  topicClassificationTableIncoming: TopicClassification[];
  keyphraseExtractionTableIncoming: KeyphraseRow[];
  awaitingTopicsRefresh: boolean;
  awaitingKeyphraseRefresh: boolean;
  isLoading: boolean;
  pendingSave: boolean;
  hasError: boolean;
  topicsActive: boolean;
  error?: AppError;
};

export type AppError<T extends Error = Error> = T & {
  status?: number;
  statusText?: string;
  message: string;
};
